import React, { ComponentProps, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Namespace } from '../../../translation/namespace';
import { FormContainerModifier, FormContext, FormUtils } from '../../../components/molecules/Form';
import { InteractionStatus } from '../../../types/InteractionStatus';
import FormsRow, { FormsRowModifier } from '../../../components/molecules/Form/components/FormsRow/FormsRow';
import { ButtonActionConfirm } from '../../../components/molecules/Btn/ButtonWrapper';
import LiveRegion from '../../../components/atoms/LiveRegion/LiveRegion';
import Form from '../../../components/molecules/Form/Form';
import UploadForm from '../partials/Forms/UploadForm';
import { DISPLAY_TYPE } from '../partials/Galleries/ThumbnailGallery';
import { murmurhash } from '../../../lib/js/hash';
import IForm from '../../../types/IForm';
import { sendLiveRegionMessage } from '../../../components/atoms/LiveRegionGlobal/LiveRegionGlobal';
import { useBlockContext, useRefreshBlock } from '../../../_lib/BlockFetcher';
import { asFetchableView } from '../../../_lib/ViewFetcher';
import EditableGallery, { EditableImage } from '../partials/Galleries/EditableGallery/EditableGallery';
import FormRadioGroup, {
  FormRadioGroupOrientation,
} from '../../../components/molecules/Form/components/FormRadioGroup/FormRadioGroup';
import { focusNonFocusableElement } from '../../../lib/DomUtils/utils';
import { useSyncImages } from '../useSyncImages';
import { Strong } from '../../../components/atoms/Strong/Strong';
import { SonForm } from '../../../_lib/Form2';
import { AnyParameter } from '../../../_lib/Form2/types/SonForm';

type ImageGalleryEditViewProps = {
  imageUploadForm: IForm;
  layoutForm: IForm;
  updateOrderForm: SonForm<AnyParameter>;
  images: EditableImage[];
};

/**
 * This Statusmessages will be used to Override the form2 default ones.
 */
const getStatusMessages = (t: TFunction) => ({
  [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(t('changesSuccess')),
  [InteractionStatus.FAIL]: FormUtils.createStatusMessage(t('changesSaveFail')),
  [InteractionStatus.SENDING]: FormUtils.createStatusMessage(
    <>
      {t('uploadInProgress')}
      <Strong> {t(`${Namespace.phrases}:pleaseWait`)}</Strong>
    </>
  ),
});

/**
 * detectChanges function compares initial and current states of
 * syncedImages array to find out if there are any changes
 * */
export function detectChanges(initialImages: EditableImage[] | null, syncedImages: EditableImage[]): boolean {
  let hasChanges = false;
  if (!initialImages && syncedImages.length === 0) {
    return hasChanges;
  }
  if (initialImages && initialImages.length !== syncedImages.length) {
    hasChanges = true;
  }
  initialImages?.forEach((initialImage: EditableImage, index: number): void => {
    const initialImageObj = initialImage.image;
    const currentImageObj = syncedImages[index]?.image;
    if (JSON.stringify(initialImageObj) !== JSON.stringify(currentImageObj)) {
      hasChanges = true;
    }
  });
  return hasChanges;
}

export function EditView({ imageUploadForm, layoutForm, updateOrderForm, images }: ImageGalleryEditViewProps) {
  const headlineRef = useRef<HTMLHeadingElement | null>(null);
  const imagesRef = useRef<{ initialImages: EditableImage[] | null; hasChanges: boolean }>({
    initialImages: null,
    hasChanges: false,
  });
  const { t } = useTranslation(Namespace.ImageGallery);
  const { refresh, delayedRefresh } = useRefreshBlock();

  const { uuid } = useBlockContext();

  const onImageUpdate = useCallback(
    ({ image }: EditableImage) =>
      sendLiveRegionMessage(
        t('conversionComplete', {
          originalFilename: image.originalFilename,
        })
      ),
    [t]
  );
  const { syncedImages, setSyncedImages, refetchImages } = useSyncImages(images, onImageUpdate);

  const headlineId = murmurhash(`${uuid + DISPLAY_TYPE.PREVIEW_AFTER_UPLOAD}`);
  // run a check after each change
  imagesRef.current.hasChanges = detectChanges(imagesRef.current.initialImages, syncedImages);

  /**
   * Refresh block on unmount (only if there are changes) -> keeps default view in sync with edit view.
   * We have to call refresh only if there are changes, in other case in stict mode it's not possible open edit view.
   */
  useEffect(() => {
    if (!imagesRef.current.initialImages) {
      imagesRef.current.initialImages = syncedImages;
    }
    return () => {
      if (imagesRef.current.hasChanges) {
        refresh();
      }
    };
  }, []);

  /**
   * We have two forms:
   * - First is for selecting and uploading images for the gallery.
   * - Second is to change display mode of the gallery.
   * */
  return (
    <div className="image-gallery">
      <Form
        id="image-gallery-image-upload-form"
        model={imageUploadForm}
        containerModifier={FormContainerModifier.NO_PADDING}
        onSuccess={() => headlineRef.current && focusNonFocusableElement(headlineRef.current)}
      >
        <UploadForm id={uuid} />
      </Form>
      {/* Gallery for preview of images after upload */}
      {syncedImages && (
        <div className="image-gallery__gallery-container">
          <EditableGallery
            id={headlineId}
            images={syncedImages}
            setSyncedImages={setSyncedImages}
            updateOrderForm={updateOrderForm}
            refetchImages={refetchImages}
            headlineRef={headlineRef}
          />
        </div>
      )}
      {syncedImages.length > 0 && (
        <Form
          id="image-gallery-update-layout-form"
          onSuccess={delayedRefresh}
          model={layoutForm}
          containerModifier={FormContainerModifier.NO_PADDING}
        >
          <FormContext.Consumer>
            {({ inputs, onChange, status, getStatus, hasChanges }: any) => (
              <>
                {inputs && inputs.layout && (
                  <FormRadioGroup
                    id={`layout-radios-${uuid}`}
                    legend={t('layout.label')}
                    orientation={FormRadioGroupOrientation.horizontal}
                    radios={inputs.layout.radioInputs}
                    labels={inputs.layout.radioInputs.reduce((acc: any, element: any) => {
                      acc[element.value] = t(`layout.radios.${element.value}`);
                      return acc;
                    }, {})}
                    selected={inputs.layout.value}
                    onChange={onChange}
                  />
                )}
                <FormsRow modifier={[FormsRowModifier.RIGHT, FormsRowModifier.NO_MARGIN]}>
                  <ButtonActionConfirm
                    type={
                      status === InteractionStatus.SUCCESS || status === InteractionStatus.SENDING || !hasChanges()
                        ? 'button'
                        : 'submit'
                    }
                    text={t('save')}
                    ariaDisabled={
                      status === InteractionStatus.SUCCESS || status === InteractionStatus.SENDING || !hasChanges()
                    }
                  />
                </FormsRow>
                <LiveRegion invisible={false} htmlTag="div">
                  {getStatus(getStatusMessages(t), status)}
                </LiveRegion>
              </>
            )}
          </FormContext.Consumer>
        </Form>
      )}
    </div>
  );
}

export const ImageGalleryEditView = asFetchableView(EditView);
export type ImageGalleryEditView = ComponentProps<typeof ImageGalleryEditView>;
